<div class="hub">

  <header class="hub__head">
    <div class="hub__head-text">
      <h1 class="hub__title">
        <i class="fa-solid fa-pen-ruler"></i>
        {{ 'TESTS_HUB.TITLE' | translate }}
      </h1>
      <p class="hub__sub">{{ 'TESTS_HUB.SUBTITLE' | translate }}</p>
    </div>

    <!-- Le rapport de toutes les matières, sans `?subject` : le bilan par
         matière vit dans le bandeau du diagnostic, mais la vue d'ensemble
         n'était atteignable qu'en retirant le paramètre à la main. -->
    <a class="btn btn--ok hub__head-action" [routerLink]="['/diagnostic-report']">
      <i class="fa-solid fa-chart-line"></i>
      {{ 'TESTS_HUB.OPEN_FULL_REPORT' | translate }}
    </a>
  </header>

  @if (errored()) {
    <!-- Erreur de chargement : jamais de matières de substitution -->
    <app-error-state titleKey="SUBJECTS.ERROR_TITLE"
                     messageKey="SUBJECTS.ERROR_DESC"
                     (retry)="reload()"></app-error-state>
  } @else if (loadingSubjects()) {
    <div class="hub__grid">
      <aside class="hub__panel">
        @for (_ of [1,2,3,4,5]; track $index) { <div class="hub__skel"></div> }
      </aside>
      <div class="hub__cards">
        @for (_ of [1,2]; track $index) { <div class="hub__skel hub__skel--tall"></div> }
      </div>
    </div>
  } @else if (subjects().length === 0) {
    <div class="hub__empty">
      <i class="fa-regular fa-folder-open"></i>
      <p>{{ 'SUBJECTS.EMPTY_DESC' | translate }}</p>
    </div>
  } @else {

  <div class="hub__grid">

    <!-- ═══ Colonne des matières ═══ -->
    <aside class="hub__panel">
      <h2 class="hub__panel-title">{{ 'TESTS_HUB.MY_SUBJECTS' | translate }}</h2>

      @for (s of subjects(); track s.id) {
        <button type="button" class="subj" [style.--c]="s.color"
                [attr.aria-current]="s.id === selectedId()"
                (click)="select(s.id)">
          <span class="subj__row">
            <!-- Le réglage des favorites vivait dans l'écran que ce hub
                 remplace : sans cette étoile, épingler deviendrait impossible.
                 Le clic s'arrête ici, sinon épingler changerait de panneau. -->
            <span class="subj__fav" role="button" tabindex="0"
                  [class.is-on]="s.isFavorite"
                  (click)="toggleFavorite(s, $event)"
                  (keydown.enter)="toggleFavorite(s, $event)"
                  [attr.aria-pressed]="s.isFavorite"
                  [attr.aria-label]="'SUBJECTS.FAVORITE' | translate">
              <i [class]="s.isFavorite ? 'fa-solid fa-star' : 'fa-regular fa-star'"></i>
            </span>
            <span class="subj__name">{{ s.name }}</span>
            <span class="subj__pct">{{ s.progress }} %</span>
          </span>
          <span class="bar"><span [style.width.%]="s.progress"></span></span>
        </button>
      }
    </aside>

    <!-- ═══ Détail de la matière ═══ -->
    <main class="hub__cards">

      @if (loadingDetail()) {
        <div class="hub__skel hub__skel--tall"></div>
        <div class="hub__skel hub__skel--tall"></div>
      } @else {

        <!-- Le diagnostic : l'action qui prime, et le premier bloc de la page.
             Toujours affiché, même sans session ouverte ni copie rendue : le
             masquer laissait l'élève devant un écran qui ne mentionne nulle
             part le diagnostic, alors que c'est la porte d'entrée du parcours.
             Ce cas-là se dit, il ne se cache pas. Sauf si le pack de l'élève
             n'inclut pas le diagnostic (SKILL2ERA-137). -->
        @if (showsDiagnostic()) {
        <section class="hero"
                 [class.hero--todo]="!diagnosticAttempt() && diagnostic()"
                 [class.hero--closed]="!diagnosticAttempt() && !diagnostic()">
            <div class="hero__body">
              <span class="hero__label">
                {{ 'TESTS_HUB.DIAGNOSTIC_LABEL' | translate }} — {{ selected()?.name }}
              </span>
              <h3 class="hero__title">
                @if (diagnosticTitle()) {
                  {{ diagnosticTitle() }}
                } @else {
                  <span class="pill pill--muted">{{ 'TESTS_HUB.DIAGNOSTIC_CLOSED_PILL' | translate }}</span>
                }
              </h3>

              @if (diagnosticAttempt(); as a) {
                <p class="hero__meta">
                  {{ a.startedAt | date:'dd/MM/yyyy' }}
                  @if (a.percentageScore != null) { · {{ a.percentageScore }} % }
                  @if (a.passed) {
                    · <span class="pill pill--ok">{{ 'TESTS_HUB.PASSED' | translate }}</span>
                  } @else if (a.passed === false) {
                    · <span class="pill pill--ko">{{ 'TESTS_HUB.FAILED' | translate }}</span>
                  }
                </p>
              } @else {
                <!-- `as` n'est pas autorisé sur `@else if` : d'où l'imbrication. -->
                @if (diagnosticEnCours(); as s) {
                  <!-- Session ouverte et jamais rendue : ni note ni copie. Le
                       dire, plutôt que d'afficher le 0 % que porte la colonne
                       par défaut en base. -->
                  <p class="hero__meta">
                    {{ s.startedAt | date:'dd/MM/yyyy' }}
                    · <span class="pill pill--muted">{{ 'TESTS_HUB.IN_PROGRESS' | translate }}</span>
                  </p>
                } @else if (diagnostic()) {
                  <p class="hero__meta">{{ 'TESTS_HUB.DIAGNOSTIC_TODO' | translate }}</p>
                } @else {
                  <p class="hero__meta">{{ 'TESTS_HUB.DIAGNOSTIC_CLOSED' | translate }}</p>
                }
              }
            </div>

            <div class="hero__actions">
              @if (diagnosticAttempt(); as a) {
                <!-- Le bilan de la matière, pas le détail des réponses : c'est
                     le document qui porte le palier, les compétences et le plan
                     de remédiation. -->
                <a
                  class="btn btn--ok"
                  [routerLink]="['/diagnostic-report']"
                  [queryParams]="{ subject: selectedId() }"
                >
                  <i class="fa-solid fa-file-lines"></i>
                  {{ 'TESTS.OPEN_REPORT' | translate }}
                </a>

                <a class="btn btn--ghost" [routerLink]="['/student/test/results', a.sessionId]">
                  <i class="fa-solid fa-list-check"></i>
                  {{ 'TESTS_HUB.SEE_ANSWERS' | translate }}
                </a>

                <!-- « Repasser » n'a de sens que si la fenêtre est encore
                     ouverte : proposer un lien mort serait pire que rien. -->
                @if (diagnostic(); as d) {
                  <a class="btn btn--ghost" [routerLink]="['/student/test/take', d.test.id]">
                    <i class="fa-solid fa-rotate-right"></i>
                    {{ 'TESTS_HUB.RETAKE' | translate }}
                  </a>
                }
              } @else {
                <!-- `as` n'est pas autorisé sur `@else if` : d'où l'imbrication. -->
                @if (diagnosticEnCours(); as s) {
                  <!-- Reprendre, et non démarrer : le serveur rend la session
                       ouverte avec les réponses déjà saisies. Annoncer « ابدأ »
                       ferait croire à un départ de zéro. -->
                  <a class="btn" [routerLink]="['/student/test/take', s.testId]">
                    <i class="fa-solid fa-rotate-left"></i>
                    {{ 'TESTS_HUB.RESUME' | translate }}
                  </a>
                } @else if (diagnostic()) {
                  <a class="btn" [routerLink]="['/student/test/take', diagnostic()!.test.id]">
                    <i class="fa-solid fa-play"></i>
                    {{ 'TESTS_HUB.START' | translate }}
                  </a>
                }
              }
            </div>
          </section>
        }

        <!-- Compteurs de la matière -->
        <div class="stats">
          <div class="stat">
            <b>{{ stats().available }}</b>
            <span>{{ 'TESTS_HUB.STAT_AVAILABLE' | translate }}</span>
          </div>
          <div class="stat">
            <b>{{ stats().done }}</b>
            <span>{{ 'TESTS_HUB.STAT_DONE' | translate }}</span>
          </div>
          <div class="stat">
            <b>@if (stats().average != null) { {{ stats().average }} % } @else { — }</b>
            <span>{{ 'TESTS_HUB.STAT_AVERAGE' | translate }}</span>
          </div>
          <div class="stat">
            <b>{{ stats().todo }}</b>
            <span>{{ 'TESTS_HUB.STAT_TODO' | translate }}</span>
          </div>
        </div>

        <!-- Entraînement : l'élève compose son test -->
        @if (showsPractice()) {
        <section class="hero hero--practice">
          <div class="hero__body">
            <span class="hero__label">{{ 'TESTS_HUB.PRACTICE_LABEL' | translate }}</span>
            <h3 class="hero__title">{{ 'TESTS_HUB.PRACTICE_TITLE' | translate }}</h3>
            <p class="hero__meta">{{ 'TESTS_HUB.PRACTICE_HINT' | translate }}</p>
          </div>
          <div class="hero__actions">
            <a class="btn" [routerLink]="composeLink()" [queryParams]="composeParams()">
              <i class="fa-solid fa-dice"></i>
              {{ 'TESTS_HUB.COMPOSE' | translate }}
            </a>
          </div>
        </section>
        }

        <!-- Devoirs de contrôle -->
        @if (showsExams()) {
        <section class="card">
          <h2 class="card__title">{{ 'TESTS_HUB.ASSIGNMENTS' | translate }}</h2>

          @if (exams().length === 0) {
            <p class="card__hint">{{ 'TESTS_HUB.NO_ASSIGNMENT' | translate }}</p>
          } @else {
            <div class="scroll">
              <table>
                <thead>
                  <tr>
                    <th>{{ 'SUBJECT_TESTS.COL_TEST' | translate }}</th>
                    <th>{{ 'TESTS_HUB.COL_WINDOW' | translate }}</th>
                    <th>{{ 'SUBJECT_TESTS.COL_ATTEMPT' | translate }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  @for (e of exams(); track e.test.id) {
                    <tr>
                      <td>{{ e.test.title }}</td>
                      <td class="num">
                        @if (e.windowState === 'open') {
                          <span class="pill pill--ok">{{ 'TESTS_HUB.OPEN' | translate }}</span>
                        } @else {
                          {{ e.test.startDate | date:'dd/MM/yyyy' }}
                        }
                      </td>
                      <td class="num">{{ e.maxAttempts }}</td>
                      <td>
                        @if (e.windowState === 'open') {
                          <a class="link" [routerLink]="['/student/test/take', e.test.id]">
                            {{ 'TESTS_HUB.START' | translate }}
                          </a>
                        } @else {
                          <span class="pill pill--muted">{{ 'TESTS_HUB.SCHEDULED' | translate }}</span>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </section>
        }

        <!-- Copies rendues -->
        <section class="card">
          <h2 class="card__title">{{ 'TESTS_HUB.MY_ATTEMPTS' | translate }}</h2>

          <!-- Les copies rendues, comme le dit le titre. Une session ouverte
               n'en est pas une : elle apparaissait ici avec une note de 0 % et
               une durée vide, ce qui affirmait trois choses fausses à la fois.
               Elle se reprend depuis la carte du diagnostic. -->
          @if (rendues().length === 0) {
            <p class="card__hint">{{ 'SUBJECT_TESTS.EMPTY_HISTORY' | translate }}</p>
          } @else {
            <div class="scroll">
              <table>
                <thead>
                  <tr>
                    <th>{{ 'SUBJECT_TESTS.COL_DATE' | translate }}</th>
                    <th>{{ 'SUBJECT_TESTS.COL_TEST' | translate }}</th>
                    <th>{{ 'SUBJECT_TESTS.COL_ATTEMPT' | translate }}</th>
                    <th>{{ 'SUBJECT_TESTS.COL_SCORE' | translate }}</th>
                    <th>{{ 'SUBJECT_TESTS.COL_TIME' | translate }}</th>
                    <th>{{ 'SUBJECT_TESTS.COL_RESULT' | translate }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  @for (a of rendues(); track a.sessionId) {
                    <tr>
                      <td class="num">{{ a.startedAt | date:'dd/MM/yyyy' }}</td>
                      <td>{{ a.testTitle }}</td>
                      <td class="num">{{ a.attemptNumber }}</td>
                      <td class="num">
                        @if (a.percentageScore != null) { {{ a.percentageScore }} % } @else { — }
                      </td>
                      <td class="num">{{ duration(a.timeSpentSeconds) }}</td>
                      <td>
                        @if (a.passed) {
                          <span class="pill pill--ok">{{ 'TESTS_HUB.PASSED' | translate }}</span>
                        } @else if (a.passed === false) {
                          <span class="pill pill--ko">{{ 'TESTS_HUB.FAILED' | translate }}</span>
                        } @else { — }
                      </td>
                      <td>
                        <a class="link" [routerLink]="['/student/test/results', a.sessionId]">
                          {{ 'TESTS_HUB.DETAIL' | translate }}
                        </a>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            <p class="card__hint">{{ 'TESTS_HUB.HISTORY_NOTE' | translate }}</p>
          }
        </section>
      }
    </main>
  </div>
  }
</div>
